import { useState } from 'react';
import '@/App.css';
import axios from 'axios';
import { Mail, Heart, CheckCircle, TrendingUp, Users, BookOpen, Send, MessageCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState('');

  const handleNewsletterSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email,
        name: name || undefined,
      });
      setSubscribed(true);
      setEmail('');
      setName('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      {/* Header with Logo */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_7cf2acf5-0719-4d3c-9e91-0688361d9fd1/artifacts/uvdmoecd_A71F6794-433A-47FF-8F74-188098DE4C47.png" 
                alt="Cat Care Club Logo" 
                className="h-12 w-12"
                data-testid="header-logo"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Cat Care Club</h1>
                <p className="text-sm text-gray-600">Feline Longevity Enthusiasts</p>
              </div>
            </div>
            <a
              href="https://www.skool.com/cats/about?ref=bea514a2467c4a229b7af93c80030662"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center px-6 py-3 text-base font-semibold text-white bg-gradient-to-r from-purple-600 to-teal-500 rounded-lg hover:from-purple-700 hover:to-teal-600 transition-all transform hover:scale-105 shadow-lg"
              data-testid="header-cta-button"
            >
              Join FREE <Heart className="ml-2 h-5 w-5" />
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-100 via-teal-50 to-purple-50 opacity-70"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8" data-testid="hero-content">
              <div className="inline-block">
                <span className="bg-gradient-to-r from-purple-600 to-teal-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  🎉 FREE Membership - Limited Time!
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Extend Your Cat's Life by{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-teal-500">
                  5-10 Years
                </span>
              </h1>
              <p className="text-xl text-gray-700 leading-relaxed">
                Join the <strong>Cat Care Club</strong> community and discover proven strategies from our Feline Longevity Blueprint to help your beloved cat live a longer, healthier life.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://www.skool.com/cats/about?ref=bea514a2467c4a229b7af93c80030662"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-purple-600 to-teal-500 rounded-lg hover:from-purple-700 hover:to-teal-600 transition-all transform hover:scale-105 shadow-lg"
                  data-testid="hero-cta-button"
                >
                  Join FREE Today <Heart className="ml-2 h-5 w-5" />
                </a>
                <a
                  href="#resources"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-purple-600 bg-white border-2 border-purple-600 rounded-lg hover:bg-purple-50 transition-all"
                  data-testid="learn-more-button"
                >
                  Learn More
                </a>
              </div>
              <div className="flex items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span>No Credit Card Required</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span>Instant Access</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-purple-400 to-teal-400 rounded-3xl transform rotate-3"></div>
              <img
                src="https://images.unsplash.com/photo-1735618603118-89e26b0dcf6e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxQZXJzaWFuJTIwY2F0JTIwcG9ydHJhaXR8ZW58MHx8fHwxNzcwODE3MTg5fDA&ixlib=rb-4.1.0&q=85"
                alt="Beautiful Persian cat - Cat Care Club"
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-gradient-to-r from-purple-600 to-teal-500 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center text-white">
            <div data-testid="stat-members">
              <div className="text-4xl font-bold mb-2">FREE</div>
              <div className="text-purple-100">Membership Access</div>
            </div>
            <div data-testid="stat-longevity">
              <div className="text-4xl font-bold mb-2">5-10 Years</div>
              <div className="text-purple-100">Extended Lifespan</div>
            </div>
            <div data-testid="stat-savings">
              <div className="text-3xl font-bold mb-2">Save on Vet Bills</div>
              <div className="text-purple-100">Science-Backed & Experience-Tested Care</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white" id="about">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1493101561740-e745892775b2"
                alt="Cat care and bonding"
                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
                data-testid="about-image"
              />
            </div>
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_7cf2acf5-0719-4d3c-9e91-0688361d9fd1/artifacts/8j8eps9k_4E654723-4001-4B30-A67B-5AF84D2EA84E.png" 
                  alt="Cat Care Club Icon" 
                  className="h-16 w-16"
                />
                <h2 className="text-4xl font-bold text-gray-900">
                  Welcome to <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-teal-500">Cat Care Club</span>
                </h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                We're a passionate community dedicated to <strong>feline longevity</strong> and supporting cat owners like you. Our mission is simple: help your cats live longer, healthier, and happier lives.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Evidence-Based Strategies</h3>
                    <p className="text-gray-600">Proven methods to increase your cat's lifespan by 5-10 years</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-teal-100 p-3 rounded-lg">
                    <Users className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Supportive Community</h3>
                    <p className="text-gray-600">Connect with fellow cat lovers who share your passion</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <BookOpen className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Expert Resources</h3>
                    <p className="text-gray-600">Access comprehensive guides and actionable insights</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-20 bg-gradient-to-b from-purple-50 to-white" id="resources">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Your FREE Resources
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Get instant access to our comprehensive guides when you join Cat Care Club
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Resource 1 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-shadow border-t-4 border-purple-500" data-testid="resource-longevity">
              <div className="bg-gradient-to-br from-purple-600 to-teal-500 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Feline Longevity Blueprint
              </h3>
              <p className="text-gray-600 mb-6">
                Our flagship guide reveals the secrets to extending your cat's life by 5-10 years through proven nutrition, lifestyle, and preventive care strategies.
              </p>
              <ul className="space-y-2 text-gray-700 mb-6">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  Optimal nutrition plans
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  Preventive health protocols
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  Lifestyle optimization
                </li>
              </ul>
            </div>

            {/* Resource 2 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-shadow border-t-4 border-teal-500" data-testid="resource-toxicity">
              <div className="bg-gradient-to-br from-red-500 to-pink-500 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Feline Toxicity Guidelines
              </h3>
              <p className="text-gray-600 mb-6">
                Protect your cat from hidden dangers with our comprehensive toxicity guide covering common household items, foods, and plants that could harm your feline friend.
              </p>
              <ul className="space-y-2 text-gray-700 mb-6">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  Dangerous foods list
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  Toxic plants database
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  Emergency protocols
                </li>
              </ul>
            </div>

            {/* Resource 3 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-shadow border-t-4 border-purple-500" data-testid="resource-checkup">
              <div className="bg-gradient-to-br from-blue-500 to-cyan-500 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Feline Check-up Resource
              </h3>
              <p className="text-gray-600 mb-6">
                Never miss important health milestones with our complete check-up guide covering age-appropriate screenings, vaccination schedules, and wellness monitoring.
              </p>
              <ul className="space-y-2 text-gray-700 mb-6">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  Vaccination schedules
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  Health screening timelines
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  Wellness tracking tools
                </li>
              </ul>
            </div>
          </div>

          <div className="text-center mt-12">
            <a
              href="https://www.skool.com/cats/about?ref=bea514a2467c4a229b7af93c80030662"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-10 py-5 text-xl font-semibold text-white bg-gradient-to-r from-purple-600 to-teal-500 rounded-lg hover:from-purple-700 hover:to-teal-600 transition-all transform hover:scale-105 shadow-lg"
              data-testid="resources-cta-button"
            >
              Get FREE Access Now <Heart className="ml-2 h-6 w-6" />
            </a>
          </div>
        </div>
      </section>

      {/* Veterinary Image Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">
                Backed by<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-teal-500">Expert Care</span>
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our resources are built on veterinary science and proven health strategies that prioritize your cat's wellbeing at every life stage.
              </p>
              <div className="bg-purple-50 border-l-4 border-purple-600 p-6 rounded-r-lg">
                <p className="text-gray-800 italic">
                  "The right preventive care and lifestyle choices can dramatically impact your cat's lifespan and quality of life. Our mission is to make this knowledge accessible to every cat owner."
                </p>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1654895622553-cf4a29be9499"
                alt="Professional veterinary care"
                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
                data-testid="veterinary-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-gradient-to-br from-purple-600 to-teal-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Mail className="h-16 w-16 text-white mx-auto mb-6" />
          <h2 className="text-4xl font-bold text-white mb-4">
            Stay Updated on Feline Health
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Get exclusive tips, resources, and community updates delivered to your inbox
          </p>
          
          {subscribed ? (
            <div className="bg-white rounded-lg p-8 max-w-md mx-auto" data-testid="newsletter-success">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">You're All Set!</h3>
              <p className="text-gray-600">Thank you for subscribing. Check your inbox for updates!</p>
            </div>
          ) : (
            <form onSubmit={handleNewsletterSignup} className="max-w-md mx-auto">
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-6 py-4 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-4 focus:ring-purple-300"
                  data-testid="newsletter-name-input"
                />
                <input
                  type="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-6 py-4 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-4 focus:ring-purple-300"
                  data-testid="newsletter-email-input"
                />
                {error && (
                  <p className="text-red-200 text-sm" data-testid="newsletter-error">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  data-testid="newsletter-submit-button"
                >
                  {loading ? 'Subscribing...' : 'Subscribe Now'}
                  <Send className="ml-2 h-5 w-5" />
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Social Media Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Follow Us on<br />Social Media
            </h2>
            <p className="text-lg text-gray-600">
              Join our growing community and get daily tips on feline health and longevity
            </p>
          </div>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <a
              href="https://www.tiktok.com/@catcareclub"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-md hover:bg-gray-800 transition-all text-sm"
              data-testid="tiktok-link"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
              </svg>
              <span>@catcareclub</span>
            </a>
            <a
              href="https://www.instagram.com/cat.care.pro"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-md hover:from-purple-700 hover:to-pink-700 transition-all text-sm"
              data-testid="instagram-link"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              <span>@cat.care.pro</span>
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <img
              src="https://images.unsplash.com/photo-1716600356369-e35a8091135e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxjb250ZW50JTIwY2F0fGVufDB8fHx8MTc3MTI3MDc3N3ww&ixlib=rb-4.1.0&q=85"
              alt="Happy, content cat in cozy bed"
              className="w-48 h-48 mx-auto rounded-2xl object-cover shadow-xl"
              data-testid="final-cta-image"
            />
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Ready to Give Your Cat a Longer Life?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join thousands of cat lovers in Cat Care Club and start your journey to feline longevity today - completely FREE!
          </p>
          <a
            href="https://www.skool.com/cats/about?ref=bea514a2467c4a229b7af93c80030662"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-12 py-6 text-xl font-semibold text-white bg-gradient-to-r from-purple-600 to-teal-500 rounded-lg hover:from-purple-700 hover:to-teal-600 transition-all transform hover:scale-105 shadow-2xl"
            data-testid="final-cta-button"
          >
            Join Cat Care Club FREE <Heart className="ml-2 h-6 w-6" />
          </a>
          <p className="mt-6 text-gray-500 text-sm">
            No credit card required • Instant access • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_7cf2acf5-0719-4d3c-9e91-0688361d9fd1/artifacts/uvdmoecd_A71F6794-433A-47FF-8F74-188098DE4C47.png" 
                  alt="Cat Care Club Logo" 
                  className="h-10 w-10"
                />
                <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-teal-400">Cat Care Club</h3>
              </div>
              <p className="text-gray-400">
                Dedicated to extending feline lives through education, community, and proven care strategies.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-purple-400">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a href="https://www.skool.com/cats/about?ref=bea514a2467c4a229b7af93c80030662" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    Join Community
                  </a>
                </li>
                <li>
                  <a href="#resources" className="hover:text-white transition-colors">
                    Resources
                  </a>
                </li>
                <li>
                  <a href="#about" className="hover:text-white transition-colors">
                    About Us
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-teal-400">Contact</h4>
              <div className="space-y-2 text-gray-400">
                <p className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <a href="mailto:catcarepro@mail.com" className="hover:text-white transition-colors">
                    catcarepro@mail.com
                  </a>
                </p>
                <div className="flex gap-4 mt-4">
                  <a href="https://www.tiktok.com/@catcareclub" target="_blank" rel="noopener noreferrer" className="hover:text-purple-400 transition-colors">
                    TikTok
                  </a>
                  <a href="https://www.instagram.com/cat.care.pro" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors">
                    Instagram
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 Cat Care Club. All rights reserved. Built with ❤️ for cats everywhere.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;